import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@sauda/ui/kit';
import { ApiError, api } from '../../lib/api.js';

export type VisitAction = 'confirm' | 'done' | 'no-show' | 'cancel' | 'reschedule' | 'assign';

/** After a cancel or no show the broker has five seconds to undo before the request is sent. */
const UNDO_MS = 5000;

/**
 * Every write a visit can take, in one place for the list and the detail: the request, the
 * refresh, the error wording, the five second undo and starting a deal from a finished visit.
 */
export function useVisitActions() {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  /** Visits whose cancel or no show is waiting out the undo window: the screen treats them as done. */
  const [held, setHeld] = useState<ReadonlySet<string>>(new Set());
  const sending = useRef(new Set<string>());
  const pending = useRef(
    new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => void }>(),
  );

  const fail = (e: unknown) =>
    setError(
      e instanceof ApiError && e.status === 409
        ? t('visits.conflict')
        : e instanceof ApiError && e.status === 403
          ? t('visits.notYours')
          : t('visits.generic'),
    );
  const act = useMutation({
    mutationFn: async (v: { id: string; action: VisitAction; body?: unknown }) => {
      // A second tap in the same moment is the same tap.
      const key = `${v.id}:${v.action}`;
      if (sending.current.has(key)) return null;
      sending.current.add(key);
      try {
        return await api(`/visits/${v.id}/${v.action}`, { method: 'POST', body: v.body ?? {} });
      } finally {
        sending.current.delete(key);
      }
    },
    onSuccess: async () => {
      setError(null);
      await cache.invalidateQueries({ queryKey: ['visits'] });
      await cache.invalidateQueries({ queryKey: ['visit'] });
      await cache.invalidateQueries({ queryKey: ['attention'] });
      await cache.invalidateQueries({ queryKey: ['overview'] });
    },
    onError: fail,
  });
  const startDeal = useMutation({
    mutationFn: (visitId: string) =>
      api<{ id: string }>('/deals', { method: 'POST', body: { visitId } }),
    onSuccess: (deal) => void navigate(`/deals/${deal.id}`),
    onError: (e) => {
      const existing = e instanceof ApiError && e.code === 'DEAL_EXISTS' ? e.details : undefined;
      const id = (existing as { dealId?: string } | undefined)?.dealId;
      if (id !== undefined) void navigate(`/deals/${id}`);
      else fail(e);
    },
  });

  // A pending undo that is still waiting when the screen goes away is sent, not lost.
  useEffect(() => {
    const waiting = pending.current;
    return () => {
      for (const entry of waiting.values()) {
        clearTimeout(entry.timer);
        entry.run();
      }
      waiting.clear();
    };
  }, []);

  /** Sends the action after the undo window; the snackbar offers Undo until then. */
  const withUndo = (
    id: string,
    action: 'cancel' | 'no-show',
    text: string,
    onSent?: () => void,
  ) => {
    const release = () =>
      setHeld((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    const run = () => {
      pending.current.delete(id);
      act.mutate({ id, action }, { onSettled: release });
      onSent?.();
    };
    const timer = setTimeout(run, UNDO_MS);
    pending.current.set(id, { timer, run });
    setHeld((current) => new Set(current).add(id));
    snack({
      text,
      duration: UNDO_MS,
      action: {
        label: t('visits.undo'),
        onClick: () => {
          clearTimeout(timer);
          pending.current.delete(id);
          release();
        },
      },
    });
  };

  return { act, startDeal, error, setError, withUndo, held };
}
